'use-client';
import * as React from 'react';
import {
  formatClassNames,
  getDataAttributes,
} from '@wix/editor-elements-common-utils';
import MeshContainer from '@wix/thunderbolt-elements/components/MeshContainer';
import ResponsiveContainer from '@wix/thunderbolt-elements/components/ResponsiveContainer';
import semanticClassNames from '../HamburgerMenuContainer.semanticClassNames';
import type { IHamburgerMenuContainerProps } from '../HamburgerMenuContainer.props';
import { useHamburgerMenuContext } from '../../HamburgerMenuContext';
import animationClasses from './HamburgerMenuContainerAnimation.scss';
import clsx from 'clsx';

export type IHamburgerMenuContainerCommonProps = Omit<
  IHamburgerMenuContainerProps,
  'stylableClassName'
> & {
  rootClassName?: string;
  containerRootClassName?: string;
  isOverflowFixEnabled?: boolean;
};

const HamburgerMenuContainerCommon: React.FC<
  IHamburgerMenuContainerCommonProps
> = props => {
  const {
    id,
    className,
    customClassNames = [],
    children,
    hasResponsiveLayout,
    containerProps,
    meshProps,
    containerRootClassName,
    onOpen,
    onClose,
    onClick,
    onMouseEnter,
    onMouseLeave,
    rootClassName,
    isOverflowFixEnabled,
  } = props;
  const { isMenuOpen, menuContainerRef, animationState } =
    useHamburgerMenuContext();

  const childrenToRender =
    typeof children === 'function' ? children : () => children;
  React.useEffect(() => {
    if (isMenuOpen === undefined) {
      return;
    }

    isMenuOpen ? onOpen?.({ type: 'onOpen' }) : onClose?.({ type: 'onClose' });
  }, [isMenuOpen, onOpen, onClose]);

  const onMenuContainerClick = React.useCallback(
    (event: React.MouseEvent) => {
      onClick?.(event);
    },
    [onClick],
  );

  return (
    <div
      id={id}
      ref={menuContainerRef}
      tabIndex={-1}
      className={clsx(
        containerRootClassName,
        className ? className : '',
        animationClasses.root,
        isOverflowFixEnabled && animationClasses.overflowFix,
      )}
      onClick={onMenuContainerClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      data-animation-name={animationState.name}
      data-animation-state={animationState.phase}
      {...getDataAttributes(props)}
    >
      <div
        aria-hidden
        className={clsx(
          rootClassName,
          formatClassNames(semanticClassNames.root, ...customClassNames),
        )}
      />
      {hasResponsiveLayout ? (
        <ResponsiveContainer {...containerProps} tabIndex={-1}>
          {childrenToRender}
        </ResponsiveContainer>
      ) : (
        <MeshContainer {...meshProps} id={id}>
          {childrenToRender}
        </MeshContainer>
      )}
    </div>
  );
};

export default HamburgerMenuContainerCommon;
